import React, { useMemo } from 'react';
import { Row, Col, Table, Button, Badge } from 'react-bootstrap';
import { Chart } from 'react-google-charts';
import './Reports.css';

export default function BookReport(props) {
  const { searchResult } = props;

  // Compute metrics from searchResult
  const stats = useMemo(() => {
    if (!searchResult || searchResult.length === 0) {
      return { total: 0, active: 0, issued: 0, inactive: 0, activePct: 0, issuedPct: 0, inactivePct: 0 };
    }
    const first = searchResult[0] || {};
    const active = Number(first['Active'] || first.active || 0);
    const issued = Number(first['Issued'] || first.issued || 0);
    const inactive = Number(first['In-Active'] || first['Inactive'] || first.inactive || 0);
    const total = active + issued + inactive;

    const activePct = total > 0 ? Math.round((active / total) * 100) : 0;
    const issuedPct = total > 0 ? Math.round((issued / total) * 100) : 0;
    const inactivePct = total > 0 ? Math.round((inactive / total) * 100) : 0;

    return { total, active, issued, inactive, activePct, issuedPct, inactivePct };
  }, [searchResult]);

  // Chart data for Google Charts Pie / Donut
  const pieChartData = useMemo(() => {
    return [
      ['Status', 'Count'],
      ['Available on Shelf', stats.active],
      ['Currently Issued', stats.issued],
      ['In-Active / Reference', stats.inactive]
    ];
  }, [stats]);

  const pieOptions = {
    pieHole: 0.45,
    is3D: false,
    colors: ['#10b981', '#3b82f6', '#f59e0b'],
    chartArea: { width: '90%', height: '80%' },
    legend: { position: 'bottom', textStyle: { color: '#334155', fontSize: 13 } },
    tooltip: { showColorCode: true }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="reports-section">
      {/* Printable Header */}
      <div className="print-only-header">
        <h2>Vidyajyoti College of Theology - Library</h2>
        <h4>Books Inventory & Catalogue Status Report</h4>
        <p>Generated On: {new Date().toLocaleDateString('en-GB')}</p>
        <hr />
      </div>

      {/* KPI Cards Grid */}
      <div className="reports-kpi-grid">
        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-primary bg-opacity-10 text-primary">
            <i className="bi bi-journals"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-primary">{stats.total}</div>
            <div className="reports-kpi-lbl">Total Catalogued Books</div>
            <div className="reports-kpi-sub">Total accessions in library</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-success bg-opacity-10 text-success">
            <i className="bi bi-check-circle"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-success">{stats.active}</div>
            <div className="reports-kpi-lbl">Available Books ({stats.activePct}%)</div>
            <div className="reports-kpi-sub">Ready for borrowing on shelf</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-info bg-opacity-10 text-info">
            <i className="bi bi-box-arrow-up-right"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-info">{stats.issued}</div>
            <div className="reports-kpi-lbl">Currently Issued ({stats.issuedPct}%)</div>
            <div className="reports-kpi-sub">Active circulation loans</div>
          </div>
        </div>

        <div className="reports-kpi-card">
          <div className="reports-kpi-icon bg-warning bg-opacity-10 text-warning">
            <i className="bi bi-shield-exclamation"></i>
          </div>
          <div className="reports-kpi-info">
            <div className="reports-kpi-val text-warning">{stats.inactive}</div>
            <div className="reports-kpi-lbl">In-Active / Ref ({stats.inactivePct}%)</div>
            <div className="reports-kpi-sub">Reference or restricted access</div>
          </div>
        </div>
      </div>

      {/* Visual Distribution Progress Bar */}
      <div className="reports-chart-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="fw-bold text-dark small">Catalogue Inventory Distribution</span>
          <span className="text-muted small">Total: {stats.total} Items</span>
        </div>
        <div className="reports-progress-wrap">
          <div
            className="reports-progress-bar bg-success"
            style={{ width: `${stats.activePct}%` }}
            title={`Available: ${stats.active} (${stats.activePct}%)`}
          />
          <div
            className="reports-progress-bar bg-primary"
            style={{ width: `${stats.issuedPct}%` }}
            title={`Issued: ${stats.issued} (${stats.issuedPct}%)`}
          />
          <div
            className="reports-progress-bar bg-warning"
            style={{ width: `${stats.inactivePct}%` }}
            title={`In-Active: ${stats.inactive} (${stats.inactivePct}%)`}
          />
        </div>
        <div className="d-flex justify-content-between small text-muted mt-2 flex-wrap gap-2">
          <span><i className="bi bi-circle-fill text-success me-1"></i> Available: <strong>{stats.active}</strong> ({stats.activePct}%)</span>
          <span><i className="bi bi-circle-fill text-primary me-1"></i> Issued: <strong>{stats.issued}</strong> ({stats.issuedPct}%)</span>
          <span><i className="bi bi-circle-fill text-warning me-1"></i> In-Active: <strong>{stats.inactive}</strong> ({stats.inactivePct}%)</span>
        </div>
      </div>

      {/* Charts & Breakdown Table Row */}
      <Row className="g-3">
        <Col lg={6} md={12}>
          <div className="reports-chart-card h-100">
            <div className="reports-chart-title">
              <i className="bi bi-pie-chart text-primary"></i> Inventory Status Proportion
            </div>
            {stats.total > 0 ? (
              <Chart
                chartType="PieChart"
                width="100%"
                height="300px"
                data={pieChartData}
                options={pieOptions}
              />
            ) : (
              <div className="text-center py-5 text-muted">No catalogue data available</div>
            )}
          </div>
        </Col>

        <Col lg={6} md={12}>
          <div className="reports-table-card h-100">
            <div className="reports-table-header">
              <span className="fw-bold text-dark small">Inventory Breakdown Summary</span>
              <Button variant="outline-secondary" size="sm" onClick={handlePrint}>
                <i className="bi bi-printer me-1"></i> Print Summary
              </Button>
            </div>
            <div className="table-responsive">
              <Table hover className="reports-table mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Category / Status</th>
                    <th className="text-center">Count</th>
                    <th className="text-center">Share</th>
                    <th>Status Note</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="fw-bold text-success">
                        <i className="bi bi-check-circle me-1"></i> Active (Available)
                      </span>
                    </td>
                    <td className="text-center fw-bold">{stats.active}</td>
                    <td className="text-center">
                      <Badge bg="success">{stats.activePct}%</Badge>
                    </td>
                    <td className="small text-muted">Available on shelves for circulation</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="fw-bold text-primary">
                        <i className="bi bi-bookmark-check me-1"></i> Issued (On Loan)
                      </span>
                    </td>
                    <td className="text-center fw-bold">{stats.issued}</td>
                    <td className="text-center">
                      <Badge bg="primary">{stats.issuedPct}%</Badge>
                    </td>
                    <td className="small text-muted">Currently checked out by patrons</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="fw-bold text-warning">
                        <i className="bi bi-exclamation-triangle me-1"></i> In-Active / Restricted
                      </span>
                    </td>
                    <td className="text-center fw-bold">{stats.inactive}</td>
                    <td className="text-center">
                      <Badge bg="warning" text="dark">{stats.inactivePct}%</Badge>
                    </td>
                    <td className="small text-muted">Reference only, damaged, or archived</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="bg-light fw-bold">
                    <td>Total Accessions</td>
                    <td className="text-center text-primary fs-6">{stats.total}</td>
                    <td className="text-center">100%</td>
                    <td className="small text-muted">Complete Library Holdings</td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
